import { Avatar, Box, Center, Flex, Grid, Icon } from '@chakra-ui/react';
import { Recording01 } from '@kula-ai/icons/dual-tone/Recording01';
import { InterviewItem as InterviewItemType } from '../types';
import { IconText } from '../IconText';
import { MarkerPin01 } from '@kula-ai/icons/dual-tone/MarkerPin01';
import { LayersThree02 } from '@kula-ai/icons/dual-tone/LayersThree02';
import { Clock } from '@kula-ai/icons/dual-tone/Clock';
import dayjs from 'dayjs';
import { Gmeet, Teams, Zoom } from '@/assets/icons/location';
import { PhoneCall01 } from '@kula-ai/icons/dual-tone/PhoneCall01';
import { SearchItemWrapper } from './SearchItemWrapper';
import { Link } from 'react-router-dom';
import { useSearchManager } from '../search-manager';
import { HighlightedTooltipText } from '../text';
import { useDateFormat } from '@/hooks';
import { Hackerrank } from '@/assets/icons/ats';

interface InterviewItemProps {
  item: InterviewItemType;
}

const locationIconMap = {
  zoom: { icon: Zoom },
  google_meet: { icon: Gmeet },
  phone_interview: { icon: PhoneCall01 },
  onsite: { icon: MarkerPin01 },
  teams: { icon: Teams },
  hackerrank: { icon: Hackerrank },
};

export const InterviewItem = ({ item }: InterviewItemProps) => {
  const { name, location, start_time, ats_job, candidate, id } = item;
  const { onSearchClose, searchTerm } = useSearchManager();
  const { dateFormat, timeFormat } = useDateFormat();

  const dateTimeFormat = `${dateFormat.label}, ${timeFormat}`;

  return (
    <Link
      to={`/interviews/${id}`}
      style={{ textDecoration: 'none' }}
      onClick={() => {
        onSearchClose();
      }}
    >
      <SearchItemWrapper>
        <Flex alignItems="center" gap="12px">
          <Center
            width="36px"
            height="36px"
            borderRadius="xs"
            bg="pastel-3-light"
          >
            <Icon
              as={Recording01}
              color="pastel-3-dark"
              width="1rem"
              height="1rem"
            />
          </Center>
          <Flex flexDirection="column" gap="3px" flex="1">
            <Flex gap="6px">
              <HighlightedTooltipText
                variant="heading_3"
                color="primary-900"
                highlight={searchTerm}
                text={name}
                maxW="890px"
              />
            </Flex>
            <Grid
              templateColumns="fit-content(90%) fit-content(90%) fit-content(20%) fit-content(20%)"
              gap="12px"
              alignItems="center"
              width="100%"
              minW={0}
            >
              {candidate?.id && (
                <Flex gap="6px" alignItems="center" minW={0}>
                  <Avatar
                    src={candidate?.avatar}
                    name={candidate?.name}
                    width="1rem"
                    height="1rem"
                    borderRadius="xs"
                    className="avatar-xs"
                  />
                  <HighlightedTooltipText
                    text={candidate?.name}
                    variant="heading_4"
                    color="secondary-700"
                    highlight={searchTerm}
                  />
                </Flex>
              )}
              {ats_job?.id && (
                <Box minW={0}>
                  <IconText
                    icon={LayersThree02}
                    text={ats_job?.name}
                    textProps={{ maxW: '100%' }}
                    highlight={searchTerm}
                  />
                </Box>
              )}
              {start_time && dateTimeFormat && (
                <Box minW={0}>
                  <IconText
                    icon={Clock}
                    text={dayjs(start_time).format(dateTimeFormat)}
                    textProps={{ maxW: '100%' }}
                  />
                </Box>
              )}
              {location && location !== 'unspecified' && (
                <Box minW={0}>
                  <IconText
                    icon={locationIconMap[location].icon}
                    text={location?.replace('_', ' ')}
                    textProps={{ textTransform: 'capitalize', maxW: '100%' }}
                  />
                </Box>
              )}
            </Grid>
          </Flex>
        </Flex>
      </SearchItemWrapper>
    </Link>
  );
};
